import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import type {
  B2bCompletionResponseDto,
  InitialUtm,
  ProfileCompletionResponseDto,
  ProfileCookieSettings,
  ProjectDetailsResponseDto,
  ScreenerNavigationDto,
  ScreenerNavigationResponseDto,
  ScreenerQuestionsResponseDto,
  WorkEmailAvailabilityDto,
} from '@/api/core-api/types.gen';

import {
  participantProjectsControllerGetNextScreenerQuestion,
  participantProjectsControllerGetProjectB2bQuestions,
  participantProjectsControllerGetProjectDetails,
  participantProjectsControllerGetProjectProfileQuestions,
  participantProjectsControllerGetScreenerQuestions,
} from '@/api/core-api';
import { BASE_PATH } from '@/constants/config';
import { getClient } from '@/utils/get-api-client';
import { ERROR_CODES } from '@common/utils';

export interface PublicApplyQueryParams {
  projectId: string;
  inviteCode?: string;
  inviteId?: string;
}

/**
 * The three reads behind the logged-out apply flow. All are `@NoAuth` on
 * core-api, and `getClient()` with no user id still authenticates as the app
 * itself — `buildCoreApiHeaders` only adds `X-Current-UserId` when given one.
 */
export const publicProjectDetailsQueryOptions = ({
  projectId,
  inviteCode,
  inviteId,
}: PublicApplyQueryParams) =>
  queryOptions({
    queryKey: ['public-apply', 'details', projectId, inviteCode, inviteId],
    queryFn: async (): Promise<ProjectDetailsResponseDto> => {
      const response = await participantProjectsControllerGetProjectDetails({
        client: getClient(),
        path: { id: projectId },
        query: { inviteCode, inviteId },
      });

      if (!response.data) {
        throw new Error('Failed to load project');
      }

      return response.data;
    },
  });

export const publicScreenerQuestionsQueryOptions = ({
  projectId,
  inviteCode,
  inviteId,
}: PublicApplyQueryParams) =>
  queryOptions({
    queryKey: ['public-apply', 'screener', projectId, inviteCode, inviteId],
    queryFn: async (): Promise<ScreenerQuestionsResponseDto> => {
      const response = await participantProjectsControllerGetScreenerQuestions({
        client: getClient(),
        path: { id: projectId },
        query: { inviteCode, inviteId },
      });

      if (!response.data) {
        throw new Error('Failed to load screener questions');
      }

      return response.data;
    },
  });

/**
 * Every profile attribute the project targets. There is no profile to compare
 * against while logged out, so the full set is asked.
 */
export const publicProfileQuestionsQueryOptions = ({
  projectId,
}: Pick<PublicApplyQueryParams, 'projectId'>) =>
  queryOptions({
    queryKey: ['public-apply', 'profile-questions', projectId],
    queryFn: async (): Promise<ProfileCompletionResponseDto> => {
      const response =
        await participantProjectsControllerGetProjectProfileQuestions({
          client: getClient(),
          path: { projectId },
        });

      if (!response.data) {
        throw new Error('Failed to load profile questions');
      }

      return response.data;
    },
  });

/**
 * Every bespoke B2B employment field the project targets, as questions.
 * Empty for non-B2B projects. Public-apply only: the signed-in flow
 * deliberately does not collect B2B employment fields just-in-time.
 */
export const publicB2bQuestionsQueryOptions = ({
  projectId,
}: Pick<PublicApplyQueryParams, 'projectId'>) =>
  queryOptions({
    queryKey: ['public-apply', 'b2b-questions', projectId],
    queryFn: async (): Promise<B2bCompletionResponseDto> => {
      const response =
        await participantProjectsControllerGetProjectB2bQuestions({
          client: getClient(),
          path: { projectId },
        });

      if (!response.data) {
        throw new Error('Failed to load B2B questions');
      }

      return response.data;
    },
  });

/**
 * Ask the server where the skip logic leads, given every answer so far.
 * Stateless: the client sends every answer each time and the server
 * re-derives the path from scratch.
 */
export async function fetchNextScreenerQuestion(params: {
  projectId: string;
  responses: ScreenerNavigationDto['responses'];
  inviteCode?: string;
  inviteId?: string;
}): Promise<ScreenerNavigationResponseDto> {
  const { projectId, responses, inviteCode, inviteId } = params;

  const response = await participantProjectsControllerGetNextScreenerQuestion({
    client: getClient(),
    path: { id: projectId },
    query: { inviteCode, inviteId },
    body: { responses },
  });

  if (!response.data) {
    throw new Error('Failed to resolve the next question');
  }

  return response.data;
}

/**
 * Whether a work email can be used, asked when the applicant leaves the
 * work-email question rather than at submit.
 *
 * Advisory: the submit re-checks server-side, because a buffered draft can
 * go stale and someone else can verify the same address in between. Goes
 * through the BFF for the per-IP budget — it answers a question about other
 * people's data.
 *
 * Fails open. A rate-limited, offline or broken check must not strand an
 * applicant on the question: the submit guard still refuses a colliding
 * address, and refusing to advance on an inconclusive answer would be worse
 * than letting them continue.
 */
export async function checkPublicApplyWorkEmail(params: {
  projectId: string;
  email: string;
}): Promise<WorkEmailAvailabilityDto> {
  try {
    const response = await fetch(
      `${BASE_PATH}/api/participants/public-apply-work-email-check`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      },
    );

    if (!response.ok) {
      return { available: true };
    }

    return await response.json();
  } catch {
    return { available: true };
  }
}

/** Why a submit didn't land, when the client can do something about it. */
export type SubmitFailure =
  /** Already a participant — they have to log in for us to attach this to them. */
  | { kind: 'accountExists' }
  /** reCAPTCHA v3 scored too low; the v2 challenge is the way through. */
  | { kind: 'captchaChallenge' }
  /** The emailed verification code is wrong or expired — retype or resend. */
  | { kind: 'invalidCode' }
  /**
   * The work email failed the submit-time re-check: already verified on
   * another profile. Nothing was created — back to the work-email question.
   */
  | { kind: 'workEmailInUse' }
  /**
   * The work email failed the submit-time re-check: forbidden (personal)
   * domain. Nothing was created — back to the work-email question.
   */
  | { kind: 'workEmailForbiddenDomain' }
  /**
   * The account email's domain is blocked (a relay service such as duck.com).
   * Nothing was created — a different address and a new code get through.
   */
  | { kind: 'emailDomainNotAllowed' }
  /** The per-address budget for verification emails is spent for now. */
  | { kind: 'tooManyCodeRequests' }
  /** Too many submit attempts from this address or IP — the signup budget. */
  | { kind: 'tooManyAttempts' }
  /** The account was created but the application itself didn't save. */
  | { kind: 'submitFailed' }
  | { kind: 'unknown' };

export class PublicApplySubmitError extends Error {
  constructor(readonly failure: SubmitFailure) {
    super(failure.kind);
    this.name = 'PublicApplySubmitError';
  }
}

export interface SubmitPublicApplicationParams {
  projectId: string;
  firstName: string;
  lastName: string;
  email: string;
  termsAccepted: true;
  captchaToken: string;
  captchaV2Token?: string;
  /** The six digits emailed by {@link sendPublicApplyVerificationCode}. */
  verificationCode: string;
  responses: unknown[];
  profileUpdate?: Record<string, unknown>;
  /** Project-agreement acceptance, for projects that require one. */
  agree?: boolean;
  totalTime: number;
  inviteId?: string;
  timezone?: string;
  /**
   * First-touch campaign attribution. Core-api stamps it on both the account
   * this call creates and the screener response it files — the two places the
   * signed-in flow attributes separately, at signup and at save.
   */
  initialUtm?: InitialUtm;
  /**
   * The banner decision this browser recorded. Persisted onto the profile the
   * account gets, and read server-side to resolve the ad-consent grants both of
   * this request's conversions answer to.
   */
  cookieSettings?: ProfileCookieSettings;
}

/**
 * The BFF's shape, flattened. `userId` is the account this request created: the
 * browser derives the `signup_completed` dedup id from it (see
 * `signupCompletedDedupId` in `@common/utils`) and reads it for nothing else.
 * `status` gates the `screener_submitted` conversion, which a response still
 * held on a verification step has not earned.
 */
const submitResultSchema = z
  .object({
    status: z.string(),
    user: z.object({ id: z.string() }),
  })
  .transform((body) => ({
    status: body.status,
    userId: body.user.id,
  }));

export type SubmitPublicApplicationResult = z.infer<typeof submitResultSchema>;

/**
 * Create the account and file the application in one request.
 *
 * Goes through the BFF because it needs the reCAPTCHA secret, the signup rate
 * limiter and a session cookie — none of which belong in the browser.
 */
export async function submitPublicApplication(
  params: SubmitPublicApplicationParams,
): Promise<SubmitPublicApplicationResult> {
  const response = await fetch(
    `${BASE_PATH}/api/participants/public-apply-submit`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    },
  );

  if (response.ok) {
    const body: unknown = await response.json();
    return submitResultSchema.parse(body);
  }

  const body: unknown = await response.json().catch(() => null);
  const error =
    typeof body === 'object' && body !== null && 'error' in body
      ? String((body as { error: unknown }).error)
      : '';

  if (response.status === 409 || error === 'accountExists') {
    throw new PublicApplySubmitError({ kind: 'accountExists' });
  }
  if (
    response.status === 422 ||
    error === ERROR_CODES.CAPTCHA_CHALLENGE_REQUIRED
  ) {
    throw new PublicApplySubmitError({ kind: 'captchaChallenge' });
  }
  // Either limiter — the BFF's signup budget or core-api's own submit
  // backstop. Both are recoverable by waiting, which is worth saying rather
  // than collapsing into the generic failure.
  if (response.status === 429 || error === 'tooManyRequests') {
    throw new PublicApplySubmitError({ kind: 'tooManyAttempts' });
  }
  if (error === 'invalidCode') {
    throw new PublicApplySubmitError({ kind: 'invalidCode' });
  }
  if (error === 'WORK_EMAIL_IN_USE') {
    throw new PublicApplySubmitError({ kind: 'workEmailInUse' });
  }
  if (error === 'WORK_EMAIL_FORBIDDEN_DOMAIN') {
    throw new PublicApplySubmitError({ kind: 'workEmailForbiddenDomain' });
  }
  if (error === ERROR_CODES.EMAIL_DOMAIN_NOT_ALLOWED) {
    throw new PublicApplySubmitError({ kind: 'emailDomainNotAllowed' });
  }
  if (error === 'submitFailed') {
    throw new PublicApplySubmitError({ kind: 'submitFailed' });
  }

  throw new PublicApplySubmitError({ kind: 'unknown' });
}

/**
 * Email the applicant a six-digit verification code, ahead of the submit
 * that will consume it. Success is silent by design — the server never says
 * whether the address already has an account.
 */
export async function sendPublicApplyVerificationCode(params: {
  projectId: string;
  email: string;
  captchaToken: string;
  captchaV2Token?: string;
}): Promise<void> {
  const response = await fetch(
    `${BASE_PATH}/api/participants/public-apply-send-code`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    },
  );

  if (response.ok) {
    return;
  }

  const body: unknown = await response.json().catch(() => null);
  const error =
    typeof body === 'object' && body !== null && 'error' in body
      ? String((body as { error: unknown }).error)
      : '';

  if (response.status === 429 || error === 'tooManyRequests') {
    throw new PublicApplySubmitError({ kind: 'tooManyCodeRequests' });
  }
  if (
    response.status === 422 ||
    error === ERROR_CODES.CAPTCHA_CHALLENGE_REQUIRED
  ) {
    throw new PublicApplySubmitError({ kind: 'captchaChallenge' });
  }

  throw new PublicApplySubmitError({ kind: 'unknown' });
}
